import { useFeatureFlag } from "@/shared/hooks/useFeatureFlag";
import { incidentsApiConfigured } from "@/shared/config/env";

/**
 * Per-station WorkOS feature flag that gates Incident Management, mirroring
 * co-review (`co-approval-workflow`) and HITL (`hitl`). It is defined and
 * toggled in the online WorkOS configuration and surfaced per station in
 * KardaAdmin → Station Settings; enabling it for a station turns the feature on
 * for that station's users when updated authentication claims arrive, at login
 * or token refresh (no redeploy).
 */
export const INCIDENT_MANAGEMENT_FEATURE_FLAG = "incident-management";

/**
 * Pure flag check — no auth context, for tests and non-hook callers.
 */
export function isIncidentManagementFlagOn(
  featureFlags?: string[] | null
): boolean {
  return (featureFlags ?? []).includes(INCIDENT_MANAGEMENT_FEATURE_FLAG);
}

/**
 * Whether Incident Management should be shown to the current user. Two gates,
 * BOTH required:
 *  1. `incidentsApiConfigured` — the incidents API base URL is wired into this
 *     build. Without it every incident request hits the wrong origin, so the
 *     feature can't be surfaced where its backend isn't configured (currently
 *     only .env.development). Infra precondition, NOT the per-station toggle.
 *  2. the station's WorkOS `incident-management` flag — the per-station gate,
 *     changed online in WorkOS with no redeploy.
 */
export function useIncidentManagementEnabled(): boolean {
  // Call the hook unconditionally (Rules of Hooks), then AND with the
  // build-time precondition.
  const flagOn = useFeatureFlag(INCIDENT_MANAGEMENT_FEATURE_FLAG);
  return incidentsApiConfigured && flagOn;
}
